import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    FiX,
    FiCheckCircle,
    FiShield,
    FiPrinter,
    FiUser,
    FiMapPin,
    FiArrowRight,
    FiCheck,
    FiCreditCard,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import storeOrderService, { saveLocalOrder } from '../../services/storeOrderService';
import { buildOrderPayload } from '../../utils/orderUtils';
import { getImageUrl } from '../../utils/imageUrl';
import { formatPriceDisplay } from '../../utils/cashPricing';
import { getStartingProductInstallment } from '../../utils/installmentCalculator';
import {
    BUSINESS_PHONE_MAIN,
    BUSINESS_WHATSAPP_CHAT_URL,
} from '../../config/businessInfo';
import OrderReceiptModal from '../common/OrderReceiptModal';
import toast from 'react-hot-toast';
import './QuickBuyModal.css';

const LAHORE_AREAS = [
    'Sanda Kalan (Showroom Area)',
    'Gulberg (I, II, III)',
    'DHA (Phases 1-8)',
    'Model Town',
    'Johar Town',
    'Faisal Town',
    'Cantt / Saddar',
    'Allama Iqbal Town',
    'Wapda Town',
    'Bahria Town',
    'Other Lahore Area',
];

const PAKISTAN_CITIES = [
    'Lahore',
    'Islamabad',
    'Rawalpindi',
    'Faisalabad',
    'Gujranwala',
    'Sialkot',
    'Multan',
    'Other City',
];

const QuickBuyModal = ({
    isOpen,
    onClose,
    product,
    cashUnitPrice,
    initialQuantity = 1,
    purchaseMode = 'cash',
    installmentCalc,
    selectedPlan,
    onSuccess,
}) => {
    const [quantity, setQuantity] = useState(initialQuantity);
    const [submitting, setSubmitting] = useState(false);
    const isSubmittingRef = useRef(false);
    const [animationStage, setAnimationStage] = useState('idle'); // 'idle' | 'packing' | 'shipping' | 'completed'
    const [orderSuccess, setOrderSuccess] = useState(null);
    const [isReceiptOpen, setIsReceiptOpen] = useState(false);
    const [receiptPrintKey, setReceiptPrintKey] = useState(0);

    const [form, setForm] = useState({
        customer_name: '',
        customer_phone: '',
        customer_whatsapp: '',
        customer_email: '',
        delivery_city: 'Lahore',
        lahore_area: 'Sanda Kalan (Showroom Area)',
        delivery_address: '',
        payment_method: 'Advance Bank Transfer (Meezan / HBL)',
        delivery_time: 'Standard (Same Day / Next Day)',
        notes: '',
    });

    const [isSameWhatsapp, setIsSameWhatsapp] = useState(true);

    useEffect(() => {
        if (isOpen) {
            setQuantity(initialQuantity || 1);
            setAnimationStage('idle');
            setOrderSuccess(null);
            setIsReceiptOpen(false);
            setReceiptPrintKey(0);
            setSubmitting(false);
            isSubmittingRef.current = false;
        } else {
            setIsReceiptOpen(false);
            isSubmittingRef.current = false;
        }
    }, [isOpen, initialQuantity]);

    if (!isOpen || !product) return null;

    // Pricing calculation
    const mrp = Number(product.mrp || product.price || product.cash_price || 0);
    const unitPrice = Number(cashUnitPrice || product.cash_price || mrp * 0.95);
    const cashTotal = unitPrice * quantity;

    // 4-month plan calculation
    const fourMonthMonthly = Math.round(mrp / 4);
    const fourMonthAdvance = fourMonthMonthly;

    // Dynamic product installment plan (strictly from configured product plans)
    const resolvedInstallment = installmentCalc?.ok
        ? installmentCalc
        : getStartingProductInstallment(product);
    const instDownPayment = resolvedInstallment?.ok
        ? (resolvedInstallment.downPaymentAmount || 0) * quantity
        : 0;
    const instMonthly = resolvedInstallment?.ok
        ? (resolvedInstallment.monthlyInstallment || 0) * quantity
        : 0;

    let payableNow = cashTotal;
    let planDesc = '100% Cash Purchase (Instant Discount Applied)';
    if (purchaseMode === 'four_month') {
        payableNow = fourMonthAdvance * quantity;
        planDesc = `4-Month 0% Markup (Advance 1st Month: ${formatPriceDisplay(payableNow)})`;
    } else if (purchaseMode === 'installment' && resolvedInstallment?.ok) {
        payableNow = instDownPayment;
        const monthsText =
            resolvedInstallment.planMonths ||
            resolvedInstallment.remainingMonths ||
            selectedPlan?.months;
        const dpText =
            resolvedInstallment.downPaymentPercent ??
            selectedPlan?.down_payment_percent;
        const totalPayableVal = (resolvedInstallment.totalPayable || 0) * quantity;
        planDesc = `Installment Plan (${monthsText} Mo • ${dpText}% Down: ${formatPriceDisplay(payableNow)}, Monthly: ${formatPriceDisplay(instMonthly)}, Total: ${formatPriceDisplay(totalPayableVal)})`;
    }

    const productImage = product.image
        ? getImageUrl(product.image)
        : 'https://res.cloudinary.com/fl3tts9v/image/upload/v1790983151/category-air-conditioners_xtx2ok.jpg';

    const handlePhoneChange = (e) => {
        const val = e.target.value;
        setForm((prev) => ({
            ...prev,
            customer_phone: val,
            customer_whatsapp: isSameWhatsapp ? val : prev.customer_whatsapp,
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleAreaSelect = (area) => {
        setForm((prev) => ({ ...prev, lahore_area: area }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Prevent accidental duplicate submissions
        if (isSubmittingRef.current || submitting) {
            return;
        }

        if (!form.customer_name.trim()) {
            toast.error('Please enter your full name');
            return;
        }
        if (!form.customer_phone.trim()) {
            toast.error('Please enter your active contact phone number');
            return;
        }
        if (!form.delivery_address.trim()) {
            toast.error('Please enter your complete delivery street address');
            return;
        }

        try {
            isSubmittingRef.current = true;
            setSubmitting(true);

            const payload = buildOrderPayload({
                customer_name: form.customer_name,
                customer_phone: form.customer_phone,
                customer_whatsapp: isSameWhatsapp ? form.customer_phone : form.customer_whatsapp,
                customer_email: form.customer_email,
                delivery_city: form.delivery_city,
                lahore_area: form.lahore_area,
                delivery_address: form.delivery_address,
                delivery_time: form.delivery_time,
                payment_method: form.payment_method,
                order_type: purchaseMode === 'cash' ? 'cash' : 'installment',
                notes: `Delivery Preference: ${form.delivery_time}${form.notes.trim() ? `. Additional Notes: ${form.notes.trim()}` : ''}`,
                items: [
                    {
                        productId: product.id || product._id || 'PROD-1',
                        product_id: product.id || product._id || 'PROD-1',
                        name: product.name,
                        brand: product.brand || 'Rahber Appliance',
                        sku: product.sku || product.model || '',
                        image: productImage,
                        quantity,
                        mrp,
                        unitPrice: purchaseMode === 'cash' ? unitPrice : mrp,
                        unit_price: purchaseMode === 'cash' ? unitPrice : mrp,
                        price: purchaseMode === 'cash' ? unitPrice : mrp,
                        totalPrice: payableNow,
                        total_price: payableNow,
                        payment_plan: purchaseMode,
                        plan_details: planDesc,
                    },
                ],
                total_amount: payableNow,
                subtotal: mrp * quantity,
                discount_amount: purchaseMode === 'cash' ? Math.max(0, (mrp - unitPrice) * quantity) : 0,
            });

            const result = await storeOrderService.placeCashOrder(payload);
            const rawPlacedOrder = result?.order || payload;

            let installmentDetails = null;
            if (purchaseMode === 'four_month') {
                installmentDetails = {
                    mode: 'four_month',
                    planMonths: 4,
                    downPaymentPercent: 25,
                    downPaymentAmount: payableNow,
                    monthlyInstallment: fourMonthMonthly * quantity,
                    totalContractAmount: mrp * quantity,
                    markupRate: 0,
                };
            } else if (purchaseMode === 'installment' && resolvedInstallment?.ok) {
                installmentDetails = {
                    mode: 'installment',
                    planMonths:
                        resolvedInstallment.planMonths ||
                        resolvedInstallment.remainingMonths ||
                        selectedPlan?.months ||
                        null,
                    downPaymentPercent:
                        resolvedInstallment.downPaymentPercent ??
                        selectedPlan?.down_payment_percent ??
                        null,
                    downPaymentAmount: instDownPayment,
                    monthlyInstallment: instMonthly,
                    totalContractAmount: (resolvedInstallment.totalPayable || 0) * quantity,
                    markupRate: resolvedInstallment.markupRate ?? null,
                };
            }

            const placedOrder = {
                ...rawPlacedOrder,
                subtotal: rawPlacedOrder.subtotal ?? payload.subtotal,
                discount_amount: rawPlacedOrder.discount_amount ?? payload.discount_amount,
                _clientItems: payload.items,
                ...(installmentDetails ? { installment_details: installmentDetails } : {}),
            };
            saveLocalOrder(placedOrder);

            // Trigger Real Animated Sequence (packing -> shipping -> completed)
            setAnimationStage('packing');

            setTimeout(() => {
                setAnimationStage('shipping');
            }, 1200);

            setTimeout(() => {
                setAnimationStage('completed');
                setOrderSuccess(placedOrder);
                setSubmitting(false);
                isSubmittingRef.current = false;
                toast.success(`Order #${placedOrder.id || placedOrder.order_number} confirmed!`);
                if (onSuccess) {
                    onSuccess(placedOrder);
                }
            }, 2500);

        } catch (err) {
            console.error('Order error:', err);
            toast.error(err?.message || 'Could not place order. Please try again.');
            setSubmitting(false);
            isSubmittingRef.current = false;
            setAnimationStage('idle');
        }
    };

    const handlePrint = () => {
        setIsReceiptOpen(true);
        setReceiptPrintKey((k) => k + 1);
    };

    return (
        <div
            className="quick-buy-backdrop"
            role="presentation"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !submitting) onClose();
            }}
        >
            <div
                className="quick-buy-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Direct Showroom Order Checkout"
            >
                {/* ── Top Header Bar (Clean, Compact, No Logo, Zero Top Gap) ── */}
                <div className="quick-buy-clean-header">
                    <div className="quick-buy-header-info">
                        <div className="quick-buy-header-badge">
                            <FiShield className="quick-buy-badge-icon" />
                            <span>Direct Appliance Booking</span>
                        </div>
                        <h2 className="quick-buy-header-title">Fast Order Checkout</h2>
                        <p className="quick-buy-header-subtitle">
                            Direct appliance booking • Official showroom warranty included
                        </p>
                    </div>
                    <button
                        type="button"
                        className="quick-buy-clean-close"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label="Close dialog"
                    >
                        <FiX />
                    </button>
                </div>

                {/* ── REAL DISPATCH ANIMATION VIEW ── */}
                {(animationStage === 'packing' || animationStage === 'shipping') && (
                    <div className="order-animation-stage-box">
                        <div className="order-anim-visual">
                            {animationStage === 'packing' ? (
                                <div className="anim-packing-wrap">
                                    <svg className="anim-package-svg" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                                        <g className="box-base">
                                            <rect x="40" y="60" width="120" height="100" rx="10" fill="#92400e" stroke="#78350f" strokeWidth="3" />
                                            <path d="M 40 60 L 100 25 L 160 60 Z" fill="#b45309" stroke="#78350f" strokeWidth="2" />
                                            <rect x="90" y="25" width="20" height="135" fill="#fde68a" opacity="0.9" />
                                            <rect x="40" y="105" width="120" height="15" fill="#fde68a" opacity="0.9" />
                                        </g>
                                        <g className="box-stamp-glow">
                                            <circle cx="100" cy="112" r="30" fill="#ea580c" opacity="0.2" />
                                            <circle cx="100" cy="112" r="22" fill="#ea580c" />
                                            <text x="100" y="116" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="900">RAHBER</text>
                                        </g>
                                    </svg>
                                    <div className="anim-stage-text">
                                        <h4>Packing &amp; Sealing Product…</h4>
                                        <p>Verifying showroom box seals, serial barcode &amp; official warranty slip</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="anim-shipping-wrap">
                                    <div className="anim-van-scene">
                                        <svg className="anim-van-svg" viewBox="0 0 220 120" xmlns="http://www.w3.org/2000/svg">
                                            <rect x="20" y="30" width="120" height="55" rx="8" fill="#1D4ED8" />
                                            <path d="M 140 45 L 175 45 L 195 70 L 195 85 L 140 85 Z" fill="#1D4ED8" />
                                            <path d="M 145 49 L 170 49 L 186 68 L 145 68 Z" fill="#93c5fd" />
                                            <rect x="20" y="65" width="175" height="7" fill="#EA580C" />
                                            <text x="75" y="55" textAnchor="middle" fill="#ffffff" fontSize="10" fontWeight="800">RAHBER DISPATCH</text>
                                            <circle cx="192" cy="76" r="4" fill="#fef08a" />
                                            <g className="anim-wheel" transform="translate(60, 85)">
                                                <circle cx="0" cy="0" r="14" fill="#1e293b" />
                                                <circle cx="0" cy="0" r="7" fill="#94a3b8" />
                                            </g>
                                            <g className="anim-wheel" transform="translate(160, 85)">
                                                <circle cx="0" cy="0" r="14" fill="#1e293b" />
                                                <circle cx="0" cy="0" r="7" fill="#94a3b8" />
                                            </g>
                                        </svg>
                                        <div className="anim-road-track">
                                            <div className="road-line" />
                                        </div>
                                    </div>
                                    <div className="anim-stage-text">
                                        <h4>Queueing for Lahore Doorstep Delivery…</h4>
                                        <p>Assigned to Sanda Kalan showroom dispatch fleet</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="order-anim-progress-bar">
                            <div className={`order-anim-progress-fill stage-${animationStage}`} />
                        </div>
                    </div>
                )}

                {/* ── SUCCESS RECEIPT VIEW (Real Showroom Slip) ── */}
                {animationStage === 'completed' && orderSuccess && (
                    <div className="quick-buy-success-view">
                        <div className="quick-buy-success-banner">
                            <div className="success-icon-badge">
                                <FiCheckCircle />
                            </div>
                            <h3>Order Confirmed &amp; Queued for Delivery!</h3>
                            <p>
                                Thank you, <strong>{orderSuccess.customer_name}</strong>! Your official showroom order is booked.
                            </p>
                            <div className="order-ref-pill">
                                <span>ORDER REFERENCE:</span>
                                <strong>#{orderSuccess.id || orderSuccess.order_number}</strong>
                            </div>
                        </div>

                        {/* Order Details Breakdown */}
                        <div className="quick-buy-summary-box">
                            <div className="summary-row-item">
                                <span className="label">Item Ordered:</span>
                                <span className="val">{product?.name} ({quantity}x)</span>
                            </div>
                            <div className="summary-row-item">
                                <span className="label">Amount Payable:</span>
                                <span className="val highlight">{formatPriceDisplay(orderSuccess.total_amount || payableNow)}</span>
                            </div>
                            <div className="summary-row-item">
                                <span className="label">Payment Mode:</span>
                                <span className="val">{orderSuccess.payment_method}</span>
                            </div>
                            <div className="summary-row-item">
                                <span className="label">Delivery Address:</span>
                                <span className="val">{orderSuccess.delivery_address}</span>
                            </div>
                            <div className="summary-row-item">
                                <span className="label">Contact Phone:</span>
                                <span className="val">{orderSuccess.customer_phone}</span>
                            </div>
                        </div>

                        <div className="quick-buy-verified-note">
                            <FiShield className="shield-icon" />
                            <span>
                                Showroom Dispatch Team will call you shortly from{' '}
                                <strong>
                                    {Array.isArray(BUSINESS_PHONE_MAIN)
                                        ? BUSINESS_PHONE_MAIN[0]?.display
                                        : BUSINESS_PHONE_MAIN}
                                </strong>{' '}
                                to verify before delivery. 100% Brand Warranty guaranteed.
                            </span>
                        </div>

                        {/* Direct Action Buttons */}
                        <div className="quick-buy-success-actions">
                            <a
                                href={`${BUSINESS_WHATSAPP_CHAT_URL}&text=${encodeURIComponent(
                                    `Hello Rahber Corporation! I just placed Order #${orderSuccess.id || orderSuccess.order_number} for "${product?.name}". Total: ${formatPriceDisplay(orderSuccess.total_amount || payableNow)}. Please confirm dispatch.`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="order-btn-whatsapp"
                            >
                                <FaWhatsapp /> Confirm on WhatsApp
                            </a>

                            <button
                                type="button"
                                className="order-btn-print"
                                onClick={handlePrint}
                            >
                                <FiPrinter /> Print Receipt
                            </button>

                            <button
                                type="button"
                                className="order-btn-done"
                                onClick={onClose}
                            >
                                Done &amp; Return to Shop
                            </button>
                        </div>
                    </div>
                )}

                {/* ── ORDER SUBMISSION FORM VIEW (Idle State) ── */}
                {animationStage === 'idle' && (
                    <form onSubmit={handleSubmit} className="quick-buy-form" noValidate>
                        {/* Section 1: Selected Appliance Strip (Glassmorphic Card) */}
                        <div className="quick-buy-product-strip">
                            <img
                                src={productImage}
                                alt={product.name}
                                className="product-strip-thumb"
                            />
                            <div className="product-strip-info">
                                <span className="product-strip-brand">{product.brand || 'Rahber Appliance'}</span>
                                <h4 className="product-strip-title">{product.name}</h4>
                                <div className="product-strip-pricing">
                                    {purchaseMode === 'cash' && (
                                        <>
                                            <span className="product-strip-cash">{formatPriceDisplay(unitPrice)}</span>
                                            {mrp > unitPrice && (
                                                <span className="product-strip-mrp">{formatPriceDisplay(mrp)}</span>
                                            )}
                                            <span className="product-strip-tag">Instant Cash Discount</span>
                                        </>
                                    )}
                                    {purchaseMode === 'four_month' && (
                                        <>
                                            <span className="product-strip-cash">{formatPriceDisplay(fourMonthAdvance)} / month</span>
                                            <span className="product-strip-tag zero-markup">0% Markup (4 Mo)</span>
                                        </>
                                    )}
                                    {purchaseMode === 'installment' && (
                                        <>
                                            <span className="product-strip-cash">{formatPriceDisplay(instDownPayment)} Down</span>
                                            <span className="product-strip-mrp">+{formatPriceDisplay(instMonthly)}/mo</span>
                                            <span className="product-strip-tag installment-badge">Installment Plan</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="product-strip-qty">
                                <label>Qty:</label>
                                <div className="qty-counter">
                                    <button
                                        type="button"
                                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                                        disabled={quantity <= 1}
                                        aria-label="Decrease quantity"
                                    >
                                        -
                                    </button>
                                    <span>{quantity}</span>
                                    <button
                                        type="button"
                                        onClick={() => setQuantity((q) => q + 1)}
                                        aria-label="Increase quantity"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ── SECTION 2: Customer Information (Clean, Spacious) ── */}
                        <div className="form-section-card">
                            <div className="form-section-header">
                                <div className="section-title-wrap">
                                    <FiUser className="section-header-icon" />
                                    <h3 className="section-title-text">Customer Information</h3>
                                </div>
                            </div>

                            <div className="form-fields-grid">
                                {/* Full Name */}
                                <div className="form-field-item">
                                    <label htmlFor="customer_name">
                                        Full Name <span className="req">*</span>
                                    </label>
                                    <div className="glass-input-wrapper">
                                        <input
                                            type="text"
                                            id="customer_name"
                                            name="customer_name"
                                            required
                                            placeholder="Your Full Name"
                                            value={form.customer_name}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                {/* Contact Phone */}
                                <div className="form-field-item">
                                    <label htmlFor="customer_phone">
                                        Contact Phone Number <span className="req">*</span>
                                    </label>
                                    <div className="glass-input-wrapper phone-wrapper">
                                        <span className="phone-prefix-tag">+92</span>
                                        <input
                                            type="tel"
                                            id="customer_phone"
                                            name="customer_phone"
                                            required
                                            placeholder="Your Phone Number (e.g. 300 1234567)"
                                            value={form.customer_phone}
                                            onChange={handlePhoneChange}
                                        />
                                    </div>
                                </div>

                                {/* Optional Email */}
                                <div className="form-field-item">
                                    <label htmlFor="customer_email">
                                        Email Address <span className="opt-tag">(Optional)</span>
                                    </label>
                                    <div className="glass-input-wrapper">
                                        <input
                                            type="email"
                                            id="customer_email"
                                            name="customer_email"
                                            placeholder="Your Email Address"
                                            value={form.customer_email}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                {/* ── REAL WHATSAPP TOGGLE (NO CHEAP ORANGE CHECKBOX) ── */}
                                <div className="form-field-item full-width">
                                    <div
                                        className={`whatsapp-toggle-card ${isSameWhatsapp ? 'whatsapp-toggle-card--active' : ''}`}
                                        onClick={() => setIsSameWhatsapp((prev) => !prev)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="whatsapp-toggle-left">
                                            <span className="whatsapp-app-icon">
                                                <FaWhatsapp />
                                            </span>
                                            <div className="whatsapp-toggle-info">
                                                <span className="whatsapp-toggle-title">Same WhatsApp as Contact Phone</span>
                                                <span className="whatsapp-toggle-subtitle">Official order receipt &amp; warranty card sent on WhatsApp</span>
                                            </div>
                                        </div>
                                        <div className={`whatsapp-custom-switch ${isSameWhatsapp ? 'whatsapp-custom-switch--on' : ''}`}>
                                            <div className="whatsapp-switch-knob" />
                                        </div>
                                    </div>

                                    {!isSameWhatsapp && (
                                        <div className="whatsapp-custom-input-box">
                                            <label htmlFor="customer_whatsapp">
                                                <FaWhatsapp className="whatsapp-icon-label" /> Alternate WhatsApp Number <span className="req">*</span>
                                            </label>
                                            <div className="glass-input-wrapper phone-wrapper">
                                                <span className="phone-prefix-tag">+92</span>
                                                <input
                                                    type="tel"
                                                    id="customer_whatsapp"
                                                    name="customer_whatsapp"
                                                    placeholder="Your Alternate WhatsApp Number"
                                                    value={form.customer_whatsapp}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── SECTION 3: Delivery Address & Lahore Sector (Spacious) ── */}
                        <div className="form-section-card">
                            <div className="form-section-header">
                                <div className="section-title-wrap">
                                    <FiMapPin className="section-header-icon" />
                                    <h3 className="section-title-text">Delivery Address (Lahore)</h3>
                                </div>
                            </div>

                            <div className="form-fields-grid">
                                {/* City */}
                                <div className="form-field-item">
                                    <label htmlFor="delivery_city">
                                        Delivery City <span className="req">*</span>
                                    </label>
                                    <div className="glass-input-wrapper">
                                        <select
                                            id="delivery_city"
                                            name="delivery_city"
                                            value={form.delivery_city}
                                            onChange={handleChange}
                                        >
                                            {PAKISTAN_CITIES.map((c) => (
                                                <option key={c} value={c}>
                                                    {c === 'Lahore' ? 'Lahore (Showroom Priority - Free)' : c}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Delivery Time Preference */}
                                <div className="form-field-item">
                                    <label htmlFor="delivery_time">
                                        Delivery Timing Preference
                                    </label>
                                    <div className="glass-input-wrapper">
                                        <select
                                            id="delivery_time"
                                            name="delivery_time"
                                            value={form.delivery_time}
                                            onChange={handleChange}
                                        >
                                            <option value="Standard (Same Day / Next Day)">Anytime Today / Tomorrow</option>
                                            <option value="Morning Slot (10 AM - 2 PM)">Morning (10:00 AM to 2:00 PM)</option>
                                            <option value="Afternoon Slot (2 PM - 6 PM)">Afternoon (2:00 PM to 6:00 PM)</option>
                                            <option value="Evening Slot (6 PM - 9 PM)">Evening (6:00 PM to 9:00 PM)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Lahore Area Pills */}
                                {form.delivery_city === 'Lahore' && (
                                    <div className="form-field-item full-width">
                                        <label>
                                            Select Lahore Sector / Area <span className="req">*</span>
                                        </label>
                                        <div className="area-pills-container">
                                            {LAHORE_AREAS.map((area) => (
                                                <button
                                                    key={area}
                                                    type="button"
                                                    className={`area-glass-pill ${
                                                        form.lahore_area === area ? 'area-glass-pill--active' : ''
                                                    }`}
                                                    onClick={() => handleAreaSelect(area)}
                                                >
                                                    {area}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Complete Street Address */}
                                <div className="form-field-item full-width">
                                    <label htmlFor="delivery_address">
                                        Complete Street Address / House / Flat <span className="req">*</span>
                                    </label>
                                    <div className="glass-input-wrapper">
                                        <input
                                            type="text"
                                            id="delivery_address"
                                            name="delivery_address"
                                            required
                                            placeholder="Your Complete Delivery Address (House #, Street #, Landmark)"
                                            value={form.delivery_address}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                {/* Delivery Instructions */}
                                <div className="form-field-item full-width">
                                    <label htmlFor="notes">
                                        Special Instructions (Optional)
                                    </label>
                                    <div className="glass-input-wrapper">
                                        <input
                                            type="text"
                                            id="notes"
                                            name="notes"
                                            placeholder="Any special instructions (e.g. Call before delivery)"
                                            value={form.notes}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── SECTION 4: Payment Method Choice Cards ── */}
                        <div className="form-section-card">
                            <div className="form-section-header">
                                <div className="section-title-wrap">
                                    <FiCreditCard className="section-header-icon" />
                                    <h3 className="section-title-text">Payment Method</h3>
                                </div>
                            </div>

                            <div className="payment-options-grid">
                                {/* Bank Transfer */}
                                <div
                                    className={`payment-option-card ${
                                        form.payment_method === 'Advance Bank Transfer (Meezan / HBL)'
                                             ? 'payment-option-card--active'
                                             : ''
                                    }`}
                                    onClick={() =>
                                        setForm((p) => ({
                                            ...p,
                                            payment_method: 'Advance Bank Transfer (Meezan / HBL)',
                                        }))
                                    }
                                    role="button"
                                    tabIndex={0}
                                >
                                    <div className="poc-radio">
                                        {form.payment_method === 'Advance Bank Transfer (Meezan / HBL)' && (
                                            <div className="poc-radio-dot" />
                                        )}
                                    </div>
                                    <div className="poc-content">
                                        <div className="poc-header">
                                            <strong>Direct Bank Transfer</strong>
                                            <span className="poc-badge-rec">Recommended</span>
                                        </div>
                                        <p>Official Meezan Bank / HBL showroom IBAN provided upon confirmation.</p>
                                    </div>
                                </div>

                                {/* Showroom Pickup */}
                                <div
                                    className={`payment-option-card ${
                                        form.payment_method === 'Showroom Self-Pickup (Sanda Kalan)'
                                             ? 'payment-option-card--active'
                                             : ''
                                    }`}
                                    onClick={() =>
                                        setForm((p) => ({
                                            ...p,
                                            payment_method: 'Showroom Self-Pickup (Sanda Kalan)',
                                        }))
                                    }
                                    role="button"
                                    tabIndex={0}
                                >
                                    <div className="poc-radio">
                                        {form.payment_method === 'Showroom Self-Pickup (Sanda Kalan)' && (
                                            <div className="poc-radio-dot" />
                                        )}
                                    </div>
                                    <div className="poc-content">
                                        <div className="poc-header">
                                            <strong>Showroom Self-Pickup</strong>
                                        </div>
                                        <p>Collect &amp; pay directly at Sanda Kalan, Chandni Chowk flagship showroom.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Trust & Guarantee Badges */}
                        <div className="showroom-trust-bar">
                            <span className="trust-item">
                                <FiCheck className="trust-check" /> Free Lahore Doorstep Delivery
                            </span>
                            <span className="trust-item">
                                <FiCheck className="trust-check" /> 100% Brand Sealed Packaging
                            </span>
                            <span className="trust-item">
                                <FiCheck className="trust-check" /> Official Warranty Card Included
                            </span>
                        </div>

                        {/* Total Bar */}
                        <div className="order-total-card">
                            <div className="total-label-wrap">
                                <span className="total-title">
                                    {purchaseMode === 'cash' ? 'Total Cash Payable:' : 'Initial Payment Due:'}
                                </span>
                                <span className="total-subtitle">Includes Free Showroom Packaging &amp; Lahore Delivery</span>
                            </div>
                            <strong className="total-amount-highlight">
                                {purchaseMode === 'cash' && formatPriceDisplay(cashTotal)}
                                {purchaseMode === 'four_month' && `${formatPriceDisplay(fourMonthAdvance)} Advance`}
                                {purchaseMode === 'installment' && `${formatPriceDisplay(instDownPayment)} Down`}
                            </strong>
                        </div>

                        {/* ── Action Buttons Group (IDENTICAL MATCHED SLEEK SIZES) ── */}
                        <div className="order-actions-footer">
                            <button
                                type="submit"
                                className="order-btn-submit"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <span className="order-submit-spinner" />
                                        <span>Processing Order…</span>
                                    </>
                                ) : (
                                    <>
                                        {purchaseMode === 'cash'
                                            ? `Confirm Cash Order (${formatPriceDisplay(payableNow)})`
                                            : `Confirm Installment Application (${formatPriceDisplay(payableNow)})`}{' '}
                                        <FiArrowRight />
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                className="order-btn-cancel"
                                onClick={onClose}
                                disabled={submitting}
                            >
                                Cancel &amp; Back to Shop
                            </button>
                        </div>

                        {/* ── Trust & Policy Links Footer (Replaces awkward gap with crucial store assurances) ── */}
                        <div className="order-modal-policies-footer">
                            <div className="order-modal-guarantee">
                                <FiShield className="order-shield-icon" />
                                <span>100% Genuine Box-Packed • Official Showroom Warranty • Free Lahore Doorstep Delivery</span>
                            </div>
                            <div className="order-modal-policy-links">
                                <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                                <span className="policy-bullet">•</span>
                                <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                                <span className="policy-bullet">•</span>
                                <Link to="/warranty" target="_blank" rel="noopener noreferrer">Warranty Terms</Link>
                                <span className="policy-bullet">•</span>
                                <Link to="/returns" target="_blank" rel="noopener noreferrer">Return &amp; Refund Policy</Link>
                                <span className="policy-bullet">•</span>
                                <Link to="/contact" target="_blank" rel="noopener noreferrer">Contact Us</Link>
                            </div>
                        </div>
                    </form>
                )}
            </div>

            <OrderReceiptModal
                isOpen={isReceiptOpen}
                onClose={() => setIsReceiptOpen(false)}
                order={orderSuccess}
                fallbackContext={{
                    product,
                    quantity,
                    mrp,
                    unitPrice,
                    purchaseMode,
                    selectedPlan,
                }}
                autoPrintKey={receiptPrintKey}
            />
        </div>
    );
};

export default QuickBuyModal;
